import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Phone, Calendar, User, ExternalLink, Briefcase, XCircle, FileText, Target, CalendarClock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCalls } from '@/hooks/useCalls';
import { useTwilio } from '@/contexts/TwilioContext';
import { toast } from 'sonner';
import { RescheduleCallModal } from '@/components/calls/RescheduleCallModal';

interface CallDetailSheetProps {
  call: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CallDetailSheet({ call, open, onOpenChange }: CallDetailSheetProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { cancelScheduledCall, rescheduleCall } = useCalls();
  const { makeCall } = useTwilio();
  const [showReschedule, setShowReschedule] = useState(false);

  const { data: contact } = useQuery({
    queryKey: ['call-detail-contact', call?.contact_id],
    enabled: !!call?.contact_id,
    queryFn: async () => {
      const { data } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, primary_phone')
        .eq('id', call.contact_id)
        .maybeSingle();
      return data;
    },
  });

  const { data: deal } = useQuery({
    queryKey: ['call-detail-deal', call?.deal_id],
    enabled: !!call?.deal_id,
    queryFn: async () => {
      const { data } = await supabase
        .from('deals')
        .select('id, name')
        .eq('id', call.deal_id)
        .maybeSingle();
      return data;
    },
  });

  const { data: handler } = useQuery({
    queryKey: ['call-detail-handler', call?.handled_by_app_user_id],
    enabled: !!call?.handled_by_app_user_id,
    queryFn: async () => {
      const { data } = await supabase
        .from('app_users')
        .select('id, display_name, email')
        .eq('id', call.handled_by_app_user_id)
        .maybeSingle();
      return data;
    },
  });

  if (!call) return null;

  const scheduledDate = call.scheduled_for ? new Date(call.scheduled_for) : null;
  const isAllDay =
    !!scheduledDate &&
    (!!(call.metadata as any)?.all_day ||
      // Convention: all-day calls are stored as YYYY-MM-DDT12:00:00Z
      (scheduledDate.getUTCHours() === 12 &&
        scheduledDate.getUTCMinutes() === 0 &&
        scheduledDate.getUTCSeconds() === 0));
  const contactName = contact
    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
    : null;

  const isActionable = call.status === 'scheduled' || call.status === 'in-progress';

  const statusLabel: Record<string, string> = {
    scheduled: 'Agendada',
    'in-progress': 'Em andamento',
    completed: 'Concluída',
    canceled: 'Cancelada',
    missed: 'Perdida',
  };

  const statusColor: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-800',
    'in-progress': 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    canceled: 'bg-muted text-muted-foreground',
    missed: 'bg-red-100 text-red-800',
  };

  const invalidateAndClose = () => {
    queryClient.invalidateQueries({ queryKey: ['agenda-events'] });
    queryClient.invalidateQueries({ queryKey: ['scheduled-calls'] });
    onOpenChange(false);
  };

  const phoneNumber = call?.to_e164 || (contact as any)?.primary_phone || null;

  const handleStartCall = async () => {
    if (!phoneNumber) {
      toast.error('Número de telefone não disponível');
      return;
    }
    try {
      await makeCall(phoneNumber, call.contact_id, call.lead_id);
      await supabase
        .from('calls')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', call.id);
      toast.success('Ligação iniciada');
      invalidateAndClose();
    } catch (err) {
      toast.error('Erro ao iniciar ligação');
    }
  };

  const handleCancel = async () => {
    try {
      await cancelScheduledCall(call.id);
      toast.success('Ligação cancelada');
      invalidateAndClose();
    } catch (err) {
      toast.error('Erro ao cancelar ligação');
    }
  };

  const handleReschedule = async (callId: string, newScheduledFor: string, reason?: string) => {
    await rescheduleCall(callId, newScheduledFor, reason);
    invalidateAndClose();
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
          <SheetHeader className="pb-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge className={statusColor[call.status] || 'bg-muted text-muted-foreground'}>
                {statusLabel[call.status] || call.status}
              </Badge>
              <Badge variant="outline" className="gap-1">
                <Phone className="h-3 w-3" />
                Ligação
              </Badge>
              {call.channel && (
                <Badge variant="secondary">{call.channel}</Badge>
              )}
            </div>
            <SheetTitle className="text-left text-lg">
              {call.title || 'Ligação agendada'}
            </SheetTitle>
          </SheetHeader>

          <Separator />

          <div className="space-y-5 pt-4">
            {scheduledDate && (
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Data agendada</p>
                  <p className="text-sm text-muted-foreground">
                    {isAllDay
                      ? `${format(scheduledDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })} (sem horário definido)`
                      : format(scheduledDate, "EEEE, d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              </div>
            )}

            {phoneNumber && (
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Telefone</p>
                  <p className="text-sm text-muted-foreground">{phoneNumber}</p>
                </div>
              </div>
            )}

            {call.call_purpose && (
              <div className="flex items-start gap-3">
                <Target className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Propósito</p>
                  <p className="text-sm text-muted-foreground">{call.call_purpose}</p>
                </div>
              </div>
            )}

            {(call.call_agenda || call.notes) && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Notas</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {call.call_agenda || call.notes}
                  </p>
                </div>
              </div>
            )}

            {(contactName || call.contact_id) && (
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Contato</p>
                  {contactName ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => { onOpenChange(false); navigate(`/contacts/${call.contact_id}`); }}
                    >
                      {contactName}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">ID: {call.contact_id}</p>
                  )}
                </div>
              </div>
            )}

            {(deal || call.deal_id) && (
              <div className="flex items-start gap-3">
                <Briefcase className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Negociação</p>
                  {deal ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => { onOpenChange(false); navigate(`/deals/${deal.id}`); }}
                    >
                      {deal.name}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">ID: {call.deal_id}</p>
                  )}
                </div>
              </div>
            )}

            {handler && (
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Responsável</p>
                  <p className="text-sm text-muted-foreground">
                    {handler.display_name || handler.email}
                  </p>
                </div>
              </div>
            )}
          </div>

          {isActionable && (
            <>
              <Separator className="my-5" />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleStartCall}
                >
                  <Phone className="h-4 w-4" />
                  Iniciar Ligação
                </Button>
                {call.status === 'scheduled' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                    onClick={() => setShowReschedule(true)}
                  >
                    <CalendarClock className="h-4 w-4" />
                    Remarcar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={handleCancel}
                >
                  <XCircle className="h-4 w-4" />
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {call && (
        <RescheduleCallModal
          open={showReschedule}
          onClose={() => setShowReschedule(false)}
          call={{
            id: call.id,
            title: call.title,
            scheduled_for: call.scheduled_for,
            channel: call.channel,
            rescheduled_count: call.rescheduled_count,
          }}
          onReschedule={handleReschedule}
        />
      )}
    </>
  );
}
